import { useTranslation } from 'next-i18next';
import { Fragment, useState } from 'react';

import AlertImage from '@/images/ui/undraw_alert.svg';
import BikingImage from '@/images/ui/undraw_biking.svg';
import BookImage from '@/images/ui/undraw_book.svg';
import DigitalNomadImage from '@/images/ui/undraw_digital_nomad.svg';
import DreamerImage from '@/images/ui/undraw_dreamer.svg';
import EnvironmentImage from '@/images/ui/undraw_environment.svg';
import OnlineLearningImage from '@/images/ui/undraw_online_learning.svg';
import PassingImage from '@/images/ui/undraw_passing.svg';
import SavingsImage from '@/images/ui/undraw_savings.svg';
import TravelImage from '@/images/ui/undraw_travel.svg';
import WelcomingImage from '@/images/ui/undraw_welcoming.svg';

import { project } from '@/lib/project';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { GoogleAdUnit } from '../@google/GoogleAdUnit';
const TipDialog = dynamic(() => import('@/components/route/dialogs/TipDialog'));

export function getTipImage(tipsType) {
	switch (tipsType) {
		case 'environmental_impact':
			return EnvironmentImage;
		case 'etiquette_and_manners':
			return WelcomingImage;
		case 'saving_money':
			return SavingsImage;
		case 'health_and_wellness':
			return BikingImage;
		case 'cultural_awareness':
			return DreamerImage;
		case 'digital_nomadism':
			return DigitalNomadImage;
		case 'safety_and_security':
			return AlertImage;
		case 'language_barriers':
			return OnlineLearningImage;
		case 'traveling_with_pets':
			return PassingImage;
		case 'comfort':
			return TravelImage;
		case 'sightseeing_opportunities':
			return BookImage;
		default:
			return null;
	}
}

export default function RouteTips({ tips, locale, includeAds = false }) {
	const { t } = useTranslation('common');
	const [selectedTip, setSelectedTip] = useState(null);
	if (!tips) {
		return null;
	}
	if (tips.length === 0) {
		return null;
	}
	return (
		<>
			<section id="tips">
				<div className="mx-auto text-center">
					<p className="text-lg font-semibold leading-8 tracking-tight text-blue-600">
						{t('travelTips.overtitle')}
					</p>
					<h2 className="mt-2 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
						{t('travelTips.title')}
					</h2>
				</div>
				<div className="mt-8 space-y-8">
					{tips.map((tip, index) => (
						<Fragment key={tip.tipsName}>
							<article className="relative isolate flex flex-col gap-8 md:flex-row">
								<div className="relative aspect-video shrink-0 md:aspect-square md:w-60">
									<Image
										alt={tip.tipsName}
										title={tip.tipsName}
										src={getTipImage(tip.tipsType)}
										className="size-full absolute inset-0 rounded-2xl bg-blue-50 object-contain"
									/>
									<div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-gray-900/10" />
								</div>
								<div>
									<div className="flex items-center gap-x-4 text-xs">
										<span className="relative z-10 rounded-full bg-gray-50 px-3 py-1.5 font-medium text-gray-600 hover:bg-gray-100">
											{tip.category.name}
										</span>
									</div>
									<div className="group relative max-w-xl">
										<h3 className="text-lg/6 mt-3 cursor-pointer font-semibold text-gray-900 group-hover:text-gray-600">
											<span onClick={() => setSelectedTip(tip.tipsName)}>
												<span className="absolute inset-0" />
												{tip.tipsName}
											</span>
										</h3>
										<ul>
											{tip.tipsItems.map(
												(item, index) =>
													index < 2 && (
														<li
															key={item.itemName}
															className="text-sm/6 mt-5 text-gray-600"
														>
															{index + 1}. {item.itemDescription}
														</li>
													)
											)}
										</ul>
										<button
											onClick={() => setSelectedTip(tip)}
											className="mt-4 text-sm font-medium capitalize text-blue-600 hover:text-blue-800"
										>
											{t('common:buttons.viewAll')}
										</button>
									</div>
								</div>
							</article>
							{includeAds && (index + 1) % 3 === 0 && (
								<>
									<GoogleAdUnit
										slotId={project.adSense.route.ad4}
										className="h-[280px] w-[336px] md:h-[90px] md:w-[728px] lg:h-[250px] lg:w-full"
									/>
								</>
							)}
						</Fragment>
					))}
				</div>
			</section>
			<TipDialog
				locale={locale}
				isOpen={selectedTip !== null}
				tip={selectedTip}
				onClose={setSelectedTip}
			/>
		</>
	);
}
